import { Toast } from '@Components/Common/Toast';
import ControlPanelBigBrowse from '@Components/ControlPanel/ControlPanelBigBrowse';
import { ChangePasswordModal } from '@Components/Modals/ChangePasswordModal/ChangePasswordModal';
import { PasswordResetModal } from '@Components/Modals/PasswordResetModal/PasswordResetModal';
import AboutOverlay from '@Components/Root/AboutPanel/AboutOverlay';
import AccountManagementOverlay from '@Components/Root/AccountManagementPanel/AccountManagementOverlay';
import SmartBrowserOverlay from '@Components/Root/SmartBrowserPanel.tsx/SmartBrowserOverlay';
import SideDrawerLeft from '@Components/SideDrawerLeft/SideDrawerLeft';
import SideDrawerRight from '@Components/SideDrawerRight/SideDrawerRight';
import { BigBrowseScreen } from '@Screens/BigBrowse/BigBrowseScreen';
import ChangePassword from '@Screens/ChangePassword/ChangePassword';
import { FourOhFourScreen } from '@Screens/FourOhFour/FourOhFourScreen';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { Provider } from 'react-native-paper';

import { linking2 } from './linking2';
import { injectHistoryPath } from './urlUtils';

import { DEFAULT_JOURNEY, HOME_ROUTE } from '~config/navigation';
import { useFloorsStore2 } from '~stores/floors-list';
import { useNavigationStore } from '~stores/navigation';
import type { RootStackParamList } from '~types/navigation';
import { RouteNames } from '~types/navigation';

// eslint-disable-next-line import/order
import { useBbQueueManager } from '@Components/BigBrowse/bb2/BbQueueManager';

const SupportOverlay = React.lazy(
  () => import('../Components/Root/Support/SupportOverlay')
);
const AddAddressOverlay = React.lazy(
  () => import('@Screens/AccountManagement/components/AddAddressOverlay')
);
const RemoveAddressConfirmationOverlay = React.lazy(
  () =>
    import(
      '../Components/Root/RemoveAddressConfirmationPanel/RemoveAddressConfirmationOverlay'
    )
);
const AddNewCreditCardOverlay = React.lazy(
  () =>
    import('../Components/Root/AddNewCreditCardPanel/AddNewCreditCardOverlay')
);
const RemoveCardConfirmationOverlay = React.lazy(
  () =>
    import(
      '../Components/Root/RemoveCardConfirmationOverlay/RemoveCardConfirmationOverlay'
    )
);

const QuickTipsOverlay = React.lazy(
  () => import('../Components/QuickTipsModal/QuickTipsOverlay')
);

const RootStack = createNativeStackNavigator<RootStackParamList>();

export const Navigator2 = observer(() => {
  const { navigatorRef, onReady, replaceRootFloor } = useNavigationStore();
  const { presenters } = useBbQueueManager();
  const { defaultFloorId } = useFloorsStore2();

  // if coming from 'deep-link' manipulate browser history to allow
  // 'back' button to return to base View
  const replaceBrowserHistory = () => {
    const routes = navigatorRef.current?.getRootState().routes ?? [];

    if (routes.length === 2) {
      const [base, top] = routes;
      injectHistoryPath([base, top]).then();
    }
  };

  // When coming from a 'deep-link', scroll the base view to the department focus point
  const scrollToDepartment = async () => {
    await when(() => {
      const routes = navigatorRef.current?.getRootState().routes ?? [];
      const [root] = routes;
      const rootPresenter = presenters.get(root?.key);
      const rootParam = rootPresenter?.journeyParams?.journeyParam;
      return rootParam !== undefined;
    });
    const routes = navigatorRef.current?.getRootState().routes ?? [];

    if (routes.length === 2) {
      const [root, top] = routes;
      const topPresenter = presenters.get(top.key);
      const rootPresenter = presenters.get(root.key);
      rootPresenter?.setScrollAtLaunch(false);

      try {
        await when(() => Boolean(topPresenter?.departmentInfo), {
          timeout: 5000,
        });
      } catch (err) {
        // Error can just be silent
      }
      const departmentInfo = topPresenter?.departmentInfo;

      if (
        departmentInfo?.floorId &&
        defaultFloorId.value !== departmentInfo?.floorId
      ) {
        // TODO: When replacing root floor, scrolling to focus point not working
        replaceRootFloor(departmentInfo.floorId, departmentInfo.departmentId);
      }

      if (departmentInfo?.departmentId) {
        try {
          await when(() => Boolean(rootPresenter?.layout?.scrollTargets), {
            timeout: 5000,
          });
        } catch (err) {
          // Error can just be silent
        }
        rootPresenter?.scrollToDepartmentFocusPoint(
          departmentInfo.departmentId
        );
      }
    }
  };

  useEffect(() => {
    replaceBrowserHistory();
    scrollToDepartment().then();
  }, []);

  return (
    <Provider>
      <NavigationContainer
        linking={linking2}
        ref={navigatorRef}
        onReady={onReady}
      >
        <RootStack.Navigator
          screenOptions={{ headerShown: false }}
          initialRouteName={HOME_ROUTE}
        >
          <RootStack.Screen
            name={RouteNames.BigBrowse}
            initialParams={{ journey: DEFAULT_JOURNEY }}
            component={BigBrowseScreen}
            options={{ title: 'Toscan', presentation: 'transparentModal' }}
          />
          <RootStack.Screen
            name={RouteNames.NotFound}
            component={FourOhFourScreen}
          />
          <RootStack.Screen
            name={RouteNames.ChangePassword}
            component={ChangePassword}
          />
        </RootStack.Navigator>
      </NavigationContainer>
      <SideDrawerLeft />
      <ControlPanelBigBrowse />
      <SideDrawerRight />
      <Toast />
      <SupportOverlay />
      <AboutOverlay />
      <QuickTipsOverlay />
      <AccountManagementOverlay />
      <AddAddressOverlay />
      <AddNewCreditCardOverlay />
      <RemoveAddressConfirmationOverlay />
      <RemoveCardConfirmationOverlay />
      <SmartBrowserOverlay />
      <PasswordResetModal />
      <ChangePasswordModal />
    </Provider>
  );
});
